'use client';

import { selectAppUser } from '@/lib/redux/app.selectors';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

export interface InitialsValidationResult {
  isValid: boolean;
  error?: string;
  expectedInitials: string;
}

export const useInitialsValidation = () => {
  const appUser = useSelector(selectAppUser);

  const expectedInitials = useMemo(() => {
    if (!appUser?.first_name || !appUser?.last_name) {
      return '';
    }
    return (appUser.first_name[0] + appUser.last_name[0]).toUpperCase();
  }, [appUser?.first_name, appUser?.last_name]);

  const validateInitials = (inputInitials: string): InitialsValidationResult => {
    // Check if user has required name fields
    if (!appUser?.first_name) {
      return {
        isValid: false,
        error: 'User does not have first name',
        expectedInitials: '',
      };
    }

    if (!appUser?.last_name) {
      return {
        isValid: false,
        error: 'User does not have last name',
        expectedInitials: '',
      };
    }

    // Validate input format
    if (!inputInitials) {
      return {
        isValid: false,
        error: 'Must be 2 characters',
        expectedInitials,
      };
    }

    if (inputInitials.length < 2) {
      return {
        isValid: false,
        error: 'Must be 2 characters',
        expectedInitials,
      };
    }

    if (inputInitials.length > 2) {
      return {
        isValid: false,
        error: 'Maximum 2 characters',
        expectedInitials,
      };
    }

    // Check if only alphabetic characters
    if (!/^[A-Za-z]{2}$/.test(inputInitials)) {
      return {
        isValid: false,
        error: 'Only letters allowed',
        expectedInitials,
      };
    }

    // Check if initials match (case-insensitive)
    if (inputInitials.toUpperCase() !== expectedInitials) {
      return {
        isValid: false,
        error: 'Must match your initials',
        expectedInitials,
      };
    }

    return {
      isValid: true,
      expectedInitials,
    };
  };

  return {
    validateInitials,
    expectedInitials,
  };
};
